import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronsDownUp,
  ChevronsUpDown,
  Download,
  Loader2,
  MoreHorizontal,
  Printer,
  Sparkles,
} from "lucide-react";
import { JsonView, allExpanded, collapseAllNested, defaultStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FieldsPanel } from "@/components/FieldsPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { previewUrl, useGetSession } from "@/hooks/api";
import { useAppStore } from "@/store/app";
import { isEdi } from "@/lib/document-kinds";
import type { FieldRow } from "@/api/types";
import { formatDistanceToNow } from "date-fns";

export function DocumentViewer() {
  const sessionId = useAppStore((s) => s.sessionId);
  const docId = useAppStore((s) => s.selectedDocId);
  const tabByDoc = useAppStore((s) => s.viewerTabByDoc);
  const setTab = useAppStore((s) => s.setViewerTab);
  const { data: session } = useGetSession(sessionId);

  if (!sessionId || !docId) {
    return <ViewerPlaceholder />;
  }

  const info = (session?.documents ?? []).find((d) => d.id === docId);
  if (!info) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Document not found.
      </div>
    );
  }

  const isEDI = isEdi(info.kind);
  // A letterhead is just an image — only the View tab makes sense (no
  // extracted fields, no structured JSON, no raw text).
  const isLetterhead = info.kind === "letterhead";
  const showFields = !isLetterhead;
  // EDI always has a parsed DTO; synthetic PDFs carry a structured stub
  // from the renderer; uploaded PDFs have no parsed_json — hide the tab
  // in that last case so it doesn't show an empty {}.
  const hasJson = !isLetterhead && (isEDI || info.is_synthetic);
  const showRaw = isEDI;
  const storedTab = tabByDoc[docId] ?? "formatted";
  const available = new Set<string>([
    "formatted",
    ...(showFields ? ["fields"] : []),
    ...(hasJson ? ["json"] : []),
    ...(showRaw ? ["raw"] : []),
  ]);
  const tab = available.has(storedTab) ? storedTab : "formatted";
  const previewSrc = previewUrl(sessionId, docId);
  // EDI is rendered from its parsed DTO into HTML; PDFs / images are shown as-is.
  const formattedLabel = isEDI ? "Rendered" : "View";

  return (
    <main className="bg-slate-50 overflow-y-auto h-full">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-semibold">{info.filename}</span>
              {info.is_synthetic && (
                <Badge variant="violet" className="gap-1 font-mono text-[10px]">
                  <Sparkles className="w-2.5 h-2.5" />
                  synthetic
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {(info.size_bytes / 1024).toFixed(1)} KB ·{" "}
              {info.is_synthetic ? "generated" : "uploaded"}{" "}
              {formatDistanceToNow(new Date(info.uploaded_at), { addSuffix: true })}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button asChild variant="ghost" size="icon">
              <a href={previewSrc} download={info.filename} title="Download">
                <Download className="w-4 h-4" />
              </a>
            </Button>
            <Button variant="ghost" size="icon" title="More" disabled>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <Tabs
          value={tab}
          onValueChange={(v) =>
            setTab(docId, v as "formatted" | "fields" | "json" | "raw")
          }
        >
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="formatted">{formattedLabel}</TabsTrigger>
              {showFields && <TabsTrigger value="fields">Fields</TabsTrigger>}
              {hasJson && <TabsTrigger value="json">JSON</TabsTrigger>}
              {showRaw && <TabsTrigger value="raw">Raw</TabsTrigger>}
            </TabsList>
            {tab === "formatted" && <PrintButton frameId={`docframe-${docId}`} />}
          </div>

          {/* The iframe stays mounted across tab switches so the PDF / HTML
              doesn't reload every time. The other panels are conditionally
              mounted so their data only fetches when their tab is opened. */}
          <TabsContent value="formatted" className="mt-3">
            <iframe
              key={previewSrc}
              id={`docframe-${docId}`}
              title={`${info.filename} preview`}
              src={previewSrc}
              className="w-full bg-white border rounded-lg shadow-sm"
              style={{ height: "calc(100vh - var(--chrome-h, 56px) - 144px)" }}
            />
          </TabsContent>

          {showFields && tab === "fields" && (
            <TabsContent value="fields" className="mt-3">
              <FieldsTab
                sessionId={sessionId}
                docId={docId}
                maxHeight="calc(100vh - var(--chrome-h, 56px) - 164px)"
              />
            </TabsContent>
          )}

          {hasJson && tab === "json" && (
            <TabsContent value="json" className="mt-3">
              <JsonPanel docId={docId} />
            </TabsContent>
          )}

          {showRaw && tab === "raw" && (
            <TabsContent value="raw" className="mt-3">
              <RawPanel docId={docId} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </main>
  );
}

function ViewerPlaceholder() {
  const pendingCount = useAppStore((s) => s.pendingEntries.length);
  if (pendingCount > 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground text-sm">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        <div>Setting up your session…</div>
        <div className="text-[12px]">
          Documents will appear on the left as they're ready.
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
      Select a document on the left.
    </div>
  );
}

function PrintButton({ frameId }: { frameId: string }) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      onClick={() => {
        const f = document.getElementById(frameId) as HTMLIFrameElement | null;
        f?.contentWindow?.print();
      }}
    >
      <Printer className="w-3.5 h-3.5" />
      Print
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Per-tab fetchers — one round-trip per tab activation.
// ---------------------------------------------------------------------------

function FieldsTab({
  sessionId,
  docId,
  maxHeight,
}: {
  sessionId: string;
  docId: string;
  maxHeight?: string;
}) {
  const [rows, setRows] = useState<FieldRow[] | null>(null);
  useEffect(() => {
    let cancel = false;
    setRows(null);
    fetch(`/api/sessions/${sessionId}/documents/${docId}/fields`)
      .then((r) => r.json())
      .then((body) => !cancel && setRows(body?.data ?? []))
      .catch(() => !cancel && setRows([]));
    return () => {
      cancel = true;
    };
  }, [sessionId, docId]);

  if (rows == null) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-md p-6 text-center">
        No fields extracted from this document.
      </div>
    );
  }
  return <FieldsPanel fields={rows} maxHeight={maxHeight} />;
}

type ExpandMode = "default" | "all" | "collapsed";

function JsonPanel({ docId }: { docId: string }) {
  const sessionId = useAppStore((s) => s.sessionId);
  const [parsedJson, setParsedJson] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ExpandMode>("default");

  useEffect(() => {
    if (!sessionId) return;
    let cancel = false;
    setError(null);
    fetch(`/api/sessions/${sessionId}/documents/${docId}/parsed`)
      .then((r) => r.json())
      .then((body) => {
        if (cancel) return;
        setParsedJson(body?.data ?? {});
      })
      .catch((e) => {
        if (cancel) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancel = true;
    };
  }, [sessionId, docId]);

  if (error) {
    return (
      <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-4">
        {error}
      </div>
    );
  }
  if (parsedJson == null) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  // The library remounts the tree when `shouldExpandNode`'s identity
  // changes, so keying the JsonView by mode forces a re-init to apply
  // the new initial-expand policy.
  const expandFn =
    mode === "all"
      ? allExpanded
      : mode === "collapsed"
        ? collapseAllNested
        : (level: number) => level < 2;

  return (
    <div>
      <div className="flex items-center justify-end gap-1.5 mb-2 text-xs">
        <Button
          variant={mode === "all" ? "secondary" : "ghost"}
          size="sm"
          className="h-7 gap-1.5"
          onClick={() => setMode("all")}
        >
          <ChevronsUpDown className="w-3.5 h-3.5" />
          Expand all
        </Button>
        <Button
          variant={mode === "collapsed" ? "secondary" : "ghost"}
          size="sm"
          className="h-7 gap-1.5"
          onClick={() => setMode("collapsed")}
        >
          <ChevronsDownUp className="w-3.5 h-3.5" />
          Collapse all
        </Button>
        <Button
          variant={mode === "default" ? "secondary" : "ghost"}
          size="sm"
          className="h-7"
          onClick={() => setMode("default")}
        >
          Reset
        </Button>
      </div>
      <div
        className="bg-white border rounded-lg p-4 overflow-auto font-mono text-[13px] leading-relaxed"
        style={{ maxHeight: "calc(100vh - var(--chrome-h, 56px) - 204px)" }}
      >
        <JsonView
          key={mode}
          data={parsedJson as object}
          shouldExpandNode={expandFn}
          clickToExpandNode
          style={{
            ...defaultStyles,
            expandIcon: defaultStyles.expandIcon + " json-tree-chevron",
            collapseIcon: defaultStyles.collapseIcon + " json-tree-chevron",
            clickableLabel: defaultStyles.clickableLabel + " json-tree-label",
          }}
        />
      </div>
    </div>
  );
}

function RawPanel({ docId }: { docId: string }) {
  const sessionId = useAppStore((s) => s.sessionId);
  const [raw, setRaw] = useState<string | null>(null);
  const ref = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancel = false;
    fetch(`/api/sessions/${sessionId}/documents/${docId}/raw`)
      .then((r) => r.json())
      .then((body) => !cancel && setRaw((body?.data as string | null) ?? ""))
      .catch((e) => !cancel && setRaw(`Error: ${e}`));
    return () => {
      cancel = true;
    };
  }, [sessionId, docId]);

  // Normalize: each segment terminator (`~`) becomes one newline.
  // Synthetic EDI is already line-broken (`~\n`); real fixtures are not.
  // Collapse any `~`-then-whitespace run to exactly `~\n` so both paths
  // produce single-spaced output.
  const pretty = useMemo(() => (raw ?? "").replace(/~\s*/g, "~\n").trimEnd(), [raw]);

  if (raw == null) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <pre
      ref={ref}
      className="bg-white border rounded-lg p-4 overflow-auto font-mono text-[11px] leading-snug whitespace-pre-wrap break-all"
      style={{ maxHeight: "calc(100vh - var(--chrome-h, 56px) - 164px)" }}
    >
      {pretty}
    </pre>
  );
}
